/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import { stateFromHTML } from 'draft-js-import-html';

/**
 * Internal dependencies
 */
import { EMPTY_LINE_HEIGHT_DATA } from './emptyLineHeight';
import { parseInlineLineHeight } from '../../elements/text/utils/mixedTypography';
import getValidHTML from '../../utils/getValidHTML';
import formatters from './formatters';

function normalizeImportedStyles(contentState) {
  const blockMap = contentState.getBlockMap().map((block) =>
    block.set(
      'characterList',
      block.getCharacterList().map((character) => {
        const styles = character.getStyle();
        const normalizedStyles = styles.reduce(
          (result, style) =>
            Array.isArray(style)
              ? style.reduce(
                  (nestedResult, nestedStyle) => nestedResult.add(nestedStyle),
                  result
                )
              : result.add(style),
          styles.clear()
        );

        return normalizedStyles === styles
          ? character
          : character.set('style', normalizedStyles);
      })
    )
  );

  return contentState.set('blockMap', blockMap);
}

function customInlineFn(context) {
  return (element, { Style }) => {
    const styleStrings = [];

    for (const formatter of formatters) {
      const style = formatter.elementToStyle(element, context);

      if (!style) {
        continue;
      }

      if (Array.isArray(style)) {
        styleStrings.push(...style);
      } else {
        styleStrings.push(style);
      }
    }

    if (styleStrings.length === 0) {
      return null;
    }

    return Style(styleStrings);
  };
}

function getEmptyLineMarker(element) {
  return Array.from(element.querySelectorAll('span[data-st-line-height]')).find(
    (span) =>
      span.childNodes.length === 0 &&
      parseInlineLineHeight(span.getAttribute('data-st-line-height')) !== null
  );
}

// The story reducer converts saved newlines to BRs. Draft imports those as
// soft breaks, which cannot carry empty-block data. Split only paragraphs that
// contain an explicit blank marker, retaining nested inline styles via ranges.
function restoreExplicitEmptyParagraphs(html) {
  if (!html.includes('data-st-line-height')) {
    return html;
  }
  const template = document.createElement('template');
  template.innerHTML = html;
  for (const paragraph of template.content.querySelectorAll('p')) {
    const hasEmptyMarker = getEmptyLineMarker(paragraph);
    const breaks = Array.from(paragraph.querySelectorAll('br'));
    if (!hasEmptyMarker || breaks.length === 0) {
      continue;
    }
    const range = document.createRange();
    range.setStart(paragraph, 0);
    const paragraphs = [];
    const appendParagraph = () => {
      const next = paragraph.cloneNode(false);
      next.appendChild(range.cloneContents());
      if (next.textContent === '' && !next.querySelector('br')) {
        next.appendChild(document.createElement('br'));
      }
      paragraphs.push(next);
    };
    for (const br of breaks) {
      range.setEndBefore(br);
      appendParagraph();
      range.setStartAfter(br);
    }
    range.setEnd(paragraph, paragraph.childNodes.length);
    appendParagraph();
    paragraph.replaceWith(...paragraphs);
  }
  return template.innerHTML;
}

function importHTML(html = '', context = {}) {
  const hasSemanticList = /<(?:ul|ol|li)(?:\s|>)/i.test(html);
  const htmlWithBreaks = hasSemanticList
    ? getValidHTML(html)
    : html
        .split('\n')
        .map((s) => `<p>${getValidHTML(s === '' ? '<br />' : s)}</p>`)
        .join('');

  return normalizeImportedStyles(
    stateFromHTML(restoreExplicitEmptyParagraphs(htmlWithBreaks), {
      customInlineFn: customInlineFn(context),
      customBlockFn: (element) => {
        if (element.tagName !== 'P' || element.textContent !== '') {
          return null;
        }
        const marker = getEmptyLineMarker(element);
        const height = parseInlineLineHeight(
          marker?.getAttribute('data-st-line-height')
        );
        if (height === null) {
          return null;
        }
        // Draft's importer retains an empty paragraph only when it contains
        // a BR placeholder. The placeholder is not part of persisted text.
        if (!element.querySelector('br')) {
          element.appendChild(element.ownerDocument.createElement('br'));
        }
        return { type: 'unstyled', data: { [EMPTY_LINE_HEIGHT_DATA]: height } };
      },
    })
  );
}

export default importHTML;
